import { useState, useEffect } from "react";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const STATUT_C = {planifiee:"#f5a623",pending:"#f5a623",en_cours:"#5b6ef8","in-progress":"#5b6ef8",terminee:"#22d3a0",completed:"#22d3a0",annulee:"#ef4444"};
const STATUT_L = {planifiee:"Planifié",pending:"Planifié",en_cours:"En cours","in-progress":"En cours",terminee:"Terminé",completed:"Terminé",annulee:"Annulé"};

export default function DashboardTechnicien({ T, user, onOpenPV }) {
  const [interventions, setInterventions] = useState([]);
  const [tickets, setTickets]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [now, setNow]                     = useState(new Date());
  const [geoStatus, setGeoStatus]         = useState("idle"); // idle | tracking | denied

  // Géolocalisation — envoi de position toutes les 30s
  useEffect(() => {
    if (!navigator.geolocation) return;
    let watchId = null;
    let lastSent = 0;

    const sendLocation = (lat, lng) => {
      const now = Date.now();
      if (now - lastSent < 25000) return; // throttle 25s min
      lastSent = now;
      fetch(`${BASE}/users/${user.id}/location`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("nexo_token") },
        body: JSON.stringify({ lat, lng }),
      }).catch(() => {});
    };

    watchId = navigator.geolocation.watchPosition(
      pos => {
        setGeoStatus("tracking");
        sendLocation(pos.coords.latitude, pos.coords.longitude);
      },
      () => setGeoStatus("denied"),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
    );

    return () => { if (watchId !== null) navigator.geolocation.clearWatch(watchId); };
  }, [user.id]);

  const [chronoSec, setChronoSec] = useState(0);

  useEffect(()=>{
    load();
    const t1 = setInterval(()=>setNow(new Date()), 60000);
    const t2 = setInterval(()=>setChronoSec(s=>s+1), 1000);
    return()=>{ clearInterval(t1); clearInterval(t2); };
  },[]);

  const load = async () => {
    setLoading(true);
    const [iv, tk] = await Promise.all([
      fetch(`${BASE}/interventions`,{headers:h()}).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/tickets`,{headers:h()}).then(r=>r.json()).catch(()=>[]),
    ]);
    const myInts = (Array.isArray(iv)?iv:[]).filter(i=>i.technicien_id===user.id||i.technicien_nom===(user.prenom+" "+user.nom));
    setInterventions(myInts);
    setTickets(Array.isArray(tk)?tk:[]);
    setLoading(false);
  };

  const today = now.toDateString();
  const todayInts = interventions.filter(i=>{
    const d = new Date(i.date_planifiee||i.date_intervention||i.created_at);
    return d.toDateString()===today;
  });
  const activeInt = interventions.find(i=>["en_cours","in-progress"].includes(i.statut));
  const nextInt   = interventions.filter(i=>["planifiee","pending"].includes(i.statut)).sort((a,b)=>new Date(a.date_planifiee||0)-new Date(b.date_planifiee||0))[0];
  const doneCount = interventions.filter(i=>["terminee","completed"].includes(i.statut)).length;
  const totalCount= interventions.length;
  const tauxCompletion = totalCount>0?Math.round(doneCount/totalCount*100):0;

  const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    const isToday  = dt.toDateString()===today;
    const isTomorrow = dt.toDateString()===new Date(Date.now()+86400000).toDateString();
    const time = dt.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"});
    if (isToday)    return `Aujourd'hui ${time}`;
    if (isTomorrow) return `Demain ${time}`;
    return dt.toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})+" "+time;
  };

  const EST_DURATIONS = {
    installation: 240, maintenance: 120, depannage: 90, diagnostic: 60,
    "mise en service": 180, "sav": 90, default: 120,
  };

  const getEstimated = (i) => {
    const type = (i.type_intervention||"").toLowerCase();
    return Object.entries(EST_DURATIONS).find(([k]) => type.includes(k))?.[1] || EST_DURATIONS.default;
  };

  const getElapsedSec = (i) => {
    if (!i?.started_at) return 0;
    return Math.floor((Date.now()-new Date(i.started_at).getTime())/1000);
  };

  const fmtChrono = (sec) => {
    const h2=Math.floor(sec/3600),m=Math.floor((sec%3600)/60),s=sec%60;
    return `${String(h2).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  };

  const getChronoStr = (i) => {
    if (!i.started_at) return null;
    const elapsed = getElapsedSec(i);
    const h2=Math.floor(elapsed/3600),m=Math.floor((elapsed%3600)/60);
    return `${String(h2).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  };

  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300,color:T.muted}}>⏳ Chargement...</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>

      {/* Bienvenue */}
      <div style={{background:`linear-gradient(135deg,${T.accent}22,${T.purple}15)`,border:`1px solid ${T.accent}30`,borderRadius:16,padding:"20px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:T.text}}>👋 Bonjour, {user.prenom} !</div>
          <div style={{fontSize:13,color:T.muted,marginTop:4}}>
            {now.toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})} · {todayInts.length} intervention(s) aujourd'hui
          </div>
          <div style={{marginTop:8,display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,
              background:geoStatus==="tracking"?"#10b981":geoStatus==="denied"?"#ef4444":"#94a3b8",
              boxShadow:geoStatus==="tracking"?"0 0 0 0 rgba(16,185,129,0.4)":undefined,
              animation:geoStatus==="tracking"?"geo-pulse 2s infinite":undefined}}/>
            <style>{`@keyframes geo-pulse{0%{box-shadow:0 0 0 0 rgba(16,185,129,0.4)}70%{box-shadow:0 0 0 6px rgba(16,185,129,0)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}`}</style>
            <span style={{fontSize:11,color:geoStatus==="tracking"?T.success:geoStatus==="denied"?"#ef4444":T.muted}}>
              {geoStatus==="tracking"?"📍 Géolocalisation active":geoStatus==="denied"?"📍 Géolocalisation refusée":"📍 Initialisation..."}
            </span>
          </div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:13,padding:"10px 16px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:T.accent}}>{tauxCompletion}%</div>
            <div style={{fontSize:10,color:T.muted}}>Complétion</div>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:13,padding:"10px 16px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:T.success}}>{doneCount}</div>
            <div style={{fontSize:10,color:T.muted}}>Terminées</div>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:13,padding:"10px 16px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:T.warning}}>{interventions.filter(i=>["planifiee","pending"].includes(i.statut)).length}</div>
            <div style={{fontSize:10,color:T.muted}}>À faire</div>
          </div>
        </div>
      </div>

      {/* Intervention active — calculateur temps réel */}
      {activeInt&&(()=>{
        void chronoSec; // déclenche le re-render chaque seconde
        const realElSec = Math.floor((Date.now()-new Date(activeInt.started_at).getTime())/1000);
        const estMin    = getEstimated(activeInt);
        const estSec    = estMin * 60;
        const pct       = Math.min(100, Math.round(realElSec / estSec * 100));
        const overtime  = realElSec > estSec;
        const overSec   = Math.max(0, realElSec - estSec);
        const barColor  = pct < 70 ? "#10b981" : pct < 90 ? "#f59e0b" : "#ef4444";
        return (
          <div style={{background:T.card,border:`2px solid ${overtime?"#ef4444":T.accent}`,borderRadius:16,overflow:"hidden"}}>
            {/* Header */}
            <div style={{padding:"12px 18px",background:`${overtime?"#ef4444":T.accent}15`,borderBottom:`1px solid ${overtime?"#ef4444":T.accent}30`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:overtime?"#ef4444":T.accent,boxShadow:`0 0 8px ${overtime?"#ef4444":T.accent}`,animation:"geo-pulse 1.5s infinite"}}/>
                <span style={{fontSize:13,fontWeight:800,color:overtime?"#ef4444":T.accent}}>
                  {overtime?"⚠️ DÉPASSEMENT":"🔴 EN COURS"}
                </span>
              </div>
              <div style={{fontFamily:"monospace",fontSize:20,fontWeight:900,color:overtime?"#ef4444":T.accent,letterSpacing:"1px"}}>
                {fmtChrono(realElSec)}
              </div>
            </div>

            <div style={{padding:"16px 18px",display:"flex",flexDirection:"column",gap:14}}>
              <div style={{fontSize:18,fontWeight:800,color:T.text}}>{activeInt.titre}</div>

              {/* Barre de progression */}
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,fontSize:12}}>
                  <span style={{color:T.muted}}>Progression estimée</span>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{color:barColor,fontWeight:700}}>{pct}%</span>
                    {overtime&&<span style={{fontSize:11,fontWeight:700,color:"#ef4444",background:"rgba(239,68,68,0.12)",padding:"2px 8px",borderRadius:99}}>+{fmtChrono(overSec)} dépassement</span>}
                  </div>
                </div>
                <div style={{height:10,background:T.border,borderRadius:99,overflow:"hidden",position:"relative"}}>
                  <div style={{position:"absolute",inset:0,width:`${pct}%`,background:`linear-gradient(90deg,${barColor},${barColor}dd)`,borderRadius:99,transition:"width 0.5s"}}/>
                  {/* Marqueur estimation */}
                  <div style={{position:"absolute",top:0,bottom:0,left:"100%",width:2,background:"#fff3",borderRadius:1}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.muted,marginTop:4}}>
                  <span>Démarré {activeInt.started_at?new Date(activeInt.started_at).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}):""}</span>
                  <span>Estimé : {estMin < 60 ? `${estMin}min` : `${estMin/60}h`} ({activeInt.type_intervention||"standard"})</span>
                </div>
              </div>

              {/* Infos */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[["👤 Client",activeInt.client_nom||"—"],["📍 Lieu",[activeInt.client_adresse,activeInt.client_ville].filter(Boolean).join(", ")||"—"],["📞 Téléphone",activeInt.client_telephone||"—"],["🔧 Type",activeInt.type_intervention||"—"]].map(([l,v])=>(
                  <div key={l} style={{background:T.bg,borderRadius:9,padding:"9px 12px",border:`1px solid ${T.border}`}}>
                    <div style={{fontSize:10,color:T.muted,marginBottom:2}}>{l}</div>
                    <div style={{fontSize:13,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{display:"flex",gap:10}}>
                {activeInt.client_telephone&&(
                  <a href={`tel:${activeInt.client_telephone}`}
                    style={{flex:1,padding:"11px",borderRadius:12,background:`${T.success}20`,border:`1px solid ${T.success}40`,color:T.success,fontSize:13,fontWeight:700,textDecoration:"none",textAlign:"center"}}>
                    📞 Appeler
                  </a>
                )}
                {activeInt.map_lat&&(
                  <a href={`https://www.google.com/maps?q=${activeInt.map_lat},${activeInt.map_lng}`} target="_blank" rel="noreferrer"
                    style={{flex:1,padding:"11px",borderRadius:12,background:`#4285f420`,border:`1px solid #4285f440`,color:"#4285f4",fontSize:13,fontWeight:700,textDecoration:"none",textAlign:"center"}}>
                    📍 Maps
                  </a>
                )}
                <button onClick={()=>onOpenPV&&onOpenPV(activeInt)}
                  style={{flex:2,padding:"11px",borderRadius:12,background:`linear-gradient(135deg,${T.accent},${T.purple||T.accent})`,border:"none",color:"#fff",fontSize:13,fontWeight:800,cursor:"pointer"}}>
                  📋 Ouvrir le PV
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Prochaine intervention */}
      {!activeInt&&nextInt&&(
        <div style={{background:T.card,border:`1.5px solid ${T.warning}50`,borderRadius:16,padding:"16px 18px"}}>
          <div style={{fontSize:12,fontWeight:700,color:T.warning,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:10}}>⏰ Prochaine intervention</div>
          <div style={{fontSize:17,fontWeight:800,color:T.text,marginBottom:8}}>{nextInt.titre}</div>
          <div style={{display:"flex",gap:14,fontSize:12,color:T.muted,marginBottom:12,flexWrap:"wrap"}}>
            <span>👤 {nextInt.client_nom||"—"}</span>
            <span>📍 {nextInt.client_ville||"—"}</span>
            <span style={{color:T.warning,fontWeight:700}}>🕐 {formatDate(nextInt.date_planifiee)}</span>
          </div>
          <div style={{display:"flex",gap:10}}>
            {nextInt.client_telephone&&(
              <a href={`tel:${nextInt.client_telephone}`}
                style={{flex:1,padding:"10px",borderRadius:12,background:`${T.success}20`,border:`1px solid ${T.success}40`,color:T.success,fontSize:13,fontWeight:700,textDecoration:"none",textAlign:"center"}}>
                📞 Appeler
              </a>
            )}
            {nextInt.map_lat&&(
              <a href={`https://waze.com/ul?ll=${nextInt.map_lat},${nextInt.map_lng}&navigate=yes`} target="_blank" rel="noreferrer"
                style={{flex:1,padding:"10px",borderRadius:12,background:`#33ccff20`,border:`1px solid #33ccff40`,color:"#33ccff",fontSize:13,fontWeight:700,textDecoration:"none",textAlign:"center"}}>
                🚗 Waze
              </a>
            )}
            <button onClick={()=>onOpenPV&&onOpenPV(nextInt)}
              style={{flex:2,padding:"10px",borderRadius:12,background:T.accent,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              📋 Ouvrir le PV
            </button>
          </div>
        </div>
      )}

      {/* Planning du jour */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,overflow:"hidden"}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,background:T.bg,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:T.text}}>📅 Mon planning</div>
          <span style={{fontSize:12,color:T.muted}}>{interventions.length} intervention(s) au total</span>
        </div>
        {interventions.length===0 ? (
          <div style={{textAlign:"center",padding:40,color:T.muted}}>
            <div style={{fontSize:36,marginBottom:10}}>🎉</div>
            <div style={{fontSize:14,fontWeight:700,color:T.text}}>Aucune intervention assignée</div>
            <div style={{fontSize:12,marginTop:4}}>Profitez-en !</div>
          </div>
        ) : (
          <div style={{maxHeight:400,overflowY:"auto"}}>
            {interventions
              .sort((a,b)=>new Date(a.date_planifiee||a.created_at)-new Date(b.date_planifiee||b.created_at))
              .map(i=>{
                const sc = STATUT_C[i.statut]||T.muted;
                const isActive = ["en_cours","in-progress"].includes(i.statut);
                const isDone   = ["terminee","completed"].includes(i.statut);
                return(
                  <div key={i.id}
                    onClick={()=>onOpenPV&&onOpenPV(i)}
                    style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderBottom:`1px solid ${T.border}22`,cursor:"pointer",background:isActive?`${T.accent}08`:"transparent",transition:"background 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.background=`${sc}08`}
                    onMouseLeave={e=>e.currentTarget.style.background=isActive?`${T.accent}08`:"transparent"}>
                    {/* Statut icon */}
                    <div style={{width:40,height:40,borderRadius:13,background:`${sc}22`,border:`2px solid ${sc}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                      {isDone?"✅":isActive?"⚙️":"📋"}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                        <span style={{fontSize:13,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{i.titre}</span>
                        <span style={{fontSize:10,fontWeight:700,color:sc,background:`${sc}22`,padding:"2px 8px",borderRadius:20,flexShrink:0,textTransform:"uppercase"}}>{STATUT_L[i.statut]||i.statut}</span>
                      </div>
                      <div style={{fontSize:11,color:T.muted,display:"flex",gap:10,flexWrap:"wrap"}}>
                        <span>👤 {i.client_nom||"—"}</span>
                        <span>📍 {i.client_ville||"—"}</span>
                        <span style={{color:isActive?T.accent:T.muted,fontWeight:isActive?700:400}}>🕐 {formatDate(i.date_planifiee)}</span>
                        {isActive&&getChronoStr(i)&&<span style={{color:T.accent,fontWeight:700,fontFamily:"monospace"}}>⏱ {getChronoStr(i)}</span>}
                      </div>
                    </div>
                    <span style={{fontSize:16,color:T.muted,flexShrink:0}}>→</span>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Stats rapides */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[
          {ic:"✅",val:doneCount,label:"Terminées",color:T.success},
          {ic:"⚙️",val:interventions.filter(i=>["en_cours","in-progress"].includes(i.statut)).length,label:"En cours",color:T.accent},
          {ic:"⏳",val:interventions.filter(i=>["planifiee","pending"].includes(i.statut)).length,label:"Planifiées",color:T.warning},
        ].map(s=>(
          <div key={s.label} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"16px",textAlign:"center"}}>
            <div style={{fontSize:26,marginBottom:6}}>{s.ic}</div>
            <div style={{fontSize:28,fontWeight:800,color:s.color,letterSpacing:"-1px"}}>{s.val}</div>
            <div style={{fontSize:11,color:T.muted,marginTop:3}}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
